import { Message, reqGetLastMessages } from '@weblite/ts-action/message'
import { Room } from '@weblite/ts-action/room'
import {
  reqSyncRoomLastMessage,
  SyncRoomLastMessage,
} from '@weblite/ts-action/room-last-message/client'
import { Mutex } from 'async-mutex'
import AwesomeDebouncePromise from 'awesome-debounce-promise'
import * as R from 'ramda'
import { getMessages, saveMessages } from '$logic/message'
import { getRoom, storageByRoomType } from '$logic/room'
import { roomLastMessageStorage } from '$logic/room-last-message/storage'
import { request, requestNow } from '$setup/ws'
import * as sync from './offline-sync'
import { dateWithOffset } from '$setup/time-offset'

export const getRoomsLastMessage = roomLastMessageStorage.getAll

const initiatedLastMessageRooms = new Set()

type NoneToVoidFn = () => void
const onUpdateCallbacks: NoneToVoidFn[] = []

export const updateLastMessageEvent = {
  onUpdate() {
    onUpdateCallbacks.forEach(cb => cb())
  },
  addEventListenerOnce(cb: NoneToVoidFn) {
    const callback = () => {
      cb()
      const index = onUpdateCallbacks.findIndex(
        updateCb => updateCb === callback,
      )
      if (index !== -1) {
        onUpdateCallbacks.splice(index, 1)
      }
    }
    onUpdateCallbacks.push(callback)
  },
}

export async function calculateLastMessage(
  roomId: string,
  firstVisibleIid: number,
  lastVisibleIid: number,
  roomIid: number,
): Promise<Message> {
  if (firstVisibleIid && lastVisibleIid && firstVisibleIid > lastVisibleIid)
    return null

  const upperBound = Math.min(roomIid, lastVisibleIid || Infinity)

  const message = R.head(
    await getMessages(roomId, {
      iid: upperBound + 1,
      count: 1,
      position: 'PREVIOUS',
    }),
  )
  if (!message || message.iid < firstVisibleIid) return null
  return message
}

export async function updateTemporaryLastMessage(message: Message) {
  await roomLastMessageStorage.add({
    roomId: message.roomId,
    calculatedAt: dateWithOffset(),
    lastMessage: message,
  })
}

export async function updateLastMessage(roomId: string) {
  if (!roomId) return
  const room = await getRoom(roomId)
  if (!room) return

  const unset = await sync.set(roomId)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { firstVisibleIid, lastVisibleIid } =
    (await storageByRoomType(room.type).get(roomId)) || {}

  const lastMessage = await calculateLastMessage(
    roomId,
    firstVisibleIid,
    lastVisibleIid,
    room.iid,
  )

  await roomLastMessageStorage.add({
    roomId,
    calculatedAt: dateWithOffset(),
    lastMessage,
  })
  updateLastMessageEvent.onUpdate()
  initiatedLastMessageRooms.add(roomId)
  await unset()
}

export async function updateRoomLastMessages(rooms: Room[]) {
  if (!rooms.length) return

  // TODO #optimize update all at once; e.g. by keeping a single record in *keyval
  //  storage* instead of a record for each room in a separate storage.
  const unsets = await Promise.all(rooms.map(({ id }) => sync.set(id)))
  const filters = rooms.map(({ id, iid }) => ({
    roomId: id,
    iid: iid + 1,
  }))

  const {
    payload: { messages },
  } = (await requestNow(reqGetLastMessages({ filters }))) as {
    payload: { messages: Message[] }
  }
  saveMessages(messages)

  const calculatedAt = dateWithOffset()
  await roomLastMessageStorage.add(
    rooms.map(({ id }) => ({
      roomId: id,
      calculatedAt,
      lastMessage: messages.find(({ roomId }) => roomId === id) || null,
    })),
  )

  rooms.forEach(({ id }, i) => {
    initiatedLastMessageRooms.add(id)
    unsets[i]()
  })
}

export const initiateRoomLastMessageDebounce = AwesomeDebouncePromise(
  updateLastMessage,
  Infinity, // TODO why?
  {
    key: roomId => roomId,
    leading: true,
  },
)

const mutexes: Record<string, Mutex> = {}

export function getMutex(name: string) {
  if (mutexes[name]) return mutexes[name]
  mutexes[name] = new Mutex()
  return mutexes[name]
}

export const initiateRoomLastMessage = async (
  roomId: string,
): Promise<void> => {
  return getMutex(`initiateRoomLastMessage-${roomId}`).runExclusive(
    async () => {
      if (!initiatedLastMessageRooms.has(roomId))
        await initiateRoomLastMessageDebounce(roomId)
    },
  )
}

export const updateRoomLastMessage = AwesomeDebouncePromise(
  updateLastMessage,
  500,
  { key: roomId => roomId, leading: false, onlyResolvesLast: false },
)

export async function syncRoomsLastMessages() {
  const rooms = getRoomsLastMessage()

  const updatedAfter = R.reduce<number, number>(
    R.max,
    0,
    rooms.map(R.prop('calculatedAt')),
  )
  if (updatedAfter) {
    const res = await request<SyncRoomLastMessage>(
      reqSyncRoomLastMessage({ updatedAfter }),
    )
    if (res.payload) {
      await Promise.all(res.payload.roomIds.map(updateRoomLastMessage))
    }
  }

  const { items, del } = await sync.getAll()
  await Promise.all(items.map(updateRoomLastMessage))
  await del()
}

export async function getRoomLastMessage(
  roomId: string,
): Promise<Message | null> {
  const res = roomLastMessageStorage.getFromRedux(roomId)
  if (res) return res.lastMessage
  await initiateRoomLastMessage(roomId)
  return roomLastMessageStorage.getFromRedux(roomId).lastMessage
}

export function getRoomLastMessageSync(roomId: string) {
  const res = roomLastMessageStorage.getFromRedux(roomId)
  if (res) return res.lastMessage
  initiateRoomLastMessage(roomId)
  return null
}
